import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { LanguageProvider } from "@/i18n/LanguageContext";
import Header from "@/layout/Header";
import Footer from "@/layout/Footer";
import ScrollToTop from "@/layout/ScrollToTop";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Spirituality from "@/pages/Spirituality";
import CommunitiesPage from "@/pages/CommunitiesPage";
import CommunityDetailPage from "@/pages/CommunityDetailPage";
import MiamiCongress from "@/pages/MiamiCongress";
import PresentationSept2026 from "@/pages/PresentationSept2026";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Support from "@/pages/Support";
import RenewPledge from "@/pages/RenewPledge";
import DonationSuccess from "@/pages/DonationSuccess";

import { AuthProvider } from "@/admin/AuthContext";
import AuthCallback from "@/admin/AuthCallback";
import ProtectedRoute from "@/admin/ProtectedRoute";
import AdminLogin from "@/admin/AdminLogin";
import AdminLayout from "@/admin/AdminLayout";
import AdminDashboard from "@/admin/AdminDashboard";
import AdminListPage from "@/admin/AdminListPage";
import AdminEvents from "@/admin/AdminEvents";
import AdminMembers from "@/admin/AdminMembers";
import AdminCommunities from "@/admin/AdminCommunities";
import AdminShrines from "@/admin/AdminShrines";
import AdminQuotas from "@/admin/AdminQuotas";
import AdminLibrary from "@/admin/AdminLibrary";
import AdminNewsletter from "@/admin/AdminNewsletter";
import AdminNovenas from "@/admin/AdminNovenas";
import AdminAccess from "@/admin/AdminAccess";
import AdminDeclarations from "@/admin/AdminDeclarations";
import AdminDonations from "@/admin/AdminDonations";
import BusinessCard from "@/pages/BusinessCard";

import { MemberAuthProvider } from "@/members/MemberAuthContext";
import MemberLogin from "@/members/MemberLogin";
import MemberRegister from "@/members/MemberRegister";
import MemberForgotPassword from "@/members/MemberForgotPassword";
import MemberResetPassword from "@/members/MemberResetPassword";
import MemberCallback from "@/members/MemberCallback";
import MemberProtectedRoute from "@/members/MemberProtectedRoute";
import MemberLayout from "@/members/MemberLayout";
import MemberHome from "@/members/MemberHome";
import MemberPlaceholder from "@/members/MemberPlaceholder";
import MemberProfile from "@/members/MemberProfile";
import MemberDirectory from "@/members/MemberDirectory";
import MemberLibrary from "@/members/MemberLibrary";
import MemberItineraries from "@/members/MemberItineraries";
import MemberItinerariesManage from "@/members/MemberItinerariesManage";
import AdminItineraries from "@/admin/AdminItineraries";
import AdminConvocations from "@/admin/AdminConvocations";
import AdminComunicados from "@/admin/AdminComunicados";
import ConvocationRespond from "@/pages/ConvocationRespond";
import VerifyCertificate from "@/pages/VerifyCertificate";
import AdminInscricoes from "@/admin/AdminInscricoes";
import MemberQuotaSuccess from "@/members/MemberQuotaSuccess";
import MemberInvites from "@/members/MemberInvites";
import MemberSpiritual from "@/members/MemberSpiritual";
import MemberSpiritualAssessor from "@/members/MemberSpiritualAssessor";
import MemberShrines from "@/members/MemberShrines";
import MemberIntentions from "@/members/MemberIntentions";

import InviteLanding from "@/pages/InviteLanding";
import MTAChat from "@/shared/MTAChat";

function MTAChatGate() {
  const location = useLocation();
  if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/cartao/")) return null;
  return <MTAChat />;
}

function PublicShell({ children }) {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  // Detect Emergent Auth session_id during render — must be FIRST.
  // Member Google callback uses /membros/google-callback so we don't need to dispatch on hash for those.
  if (location.hash?.includes("session_id=") && !location.pathname.startsWith("/membros")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Admin */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="inscricoes" element={<AdminInscricoes />} />
        <Route path="miami" element={
          <AdminListPage overline="Interesse" title="Congresso Miami · Set 2027"
            type="miami" endpoint="/admin/rsvps?event=congresso-miami-2026"
            csvUrl="/admin/rsvps.csv?event=congresso-miami-2026"
            archiveBase="/admin/rsvps" defaultEmpty="Ainda sem manifestações de interesse." />
        } />
        <Route path="newsletter" element={<AdminNewsletter />} />
        <Route path="novenas" element={<AdminNovenas />} />
        <Route path="acessos" element={<AdminAccess />} />
        <Route path="contactos" element={
          <AdminListPage overline="Contactos" title="Mensagens recebidas"
            type="contacts" endpoint="/admin/contacts" csvUrl="/admin/contacts.csv"
            archiveBase="/admin/contacts" defaultEmpty="Ainda sem mensagens." />
        } />
        <Route path="eventos" element={<AdminEvents />} />
        <Route path="comunidades" element={<AdminCommunities />} />
        <Route path="santuarios" element={<AdminShrines />} />
        <Route path="membros" element={<AdminMembers />} />
        <Route path="cotas" element={<AdminQuotas />} />
        <Route path="biblioteca" element={<AdminLibrary />} />
        <Route path="percursos" element={<AdminItineraries />} />
        <Route path="convocatorias" element={<AdminConvocations />} />
        <Route path="comunicados" element={<AdminComunicados />} />
        <Route path="declaracoes" element={<AdminDeclarations />} />
        <Route path="donativos" element={<AdminDonations />} />
      </Route>

      {/* Members area */}
      <Route path="/membros/login" element={<MemberLogin />} />
      <Route path="/membros/registar" element={<MemberRegister />} />
      <Route path="/membros/esqueci" element={<MemberForgotPassword />} />
      <Route path="/membros/recuperar" element={<MemberResetPassword />} />
      <Route path="/membros/google-callback" element={<MemberCallback />} />
      <Route path="/membros" element={<MemberProtectedRoute><MemberLayout /></MemberProtectedRoute>}>
        <Route index element={<MemberHome />} />
        <Route path="quota-confirmada" element={<MemberQuotaSuccess />} />
        <Route path="perfil" element={<MemberProfile />} />
        <Route path="percursos" element={<MemberItineraries />} />
        <Route path="percursos/gestao" element={<MemberItinerariesManage />} />
        {/* Rotas antigas — redirecionam para a nova nomenclatura */}
        <Route path="itinerarios" element={<Navigate to="/membros/percursos" replace />} />
        <Route path="itinerarios/gestao" element={<Navigate to="/membros/percursos/gestao" replace />} />
        <Route path="biblioteca" element={<MemberLibrary />} />
        <Route path="documentos" element={<Navigate to="/membros/biblioteca" replace />} />
        <Route path="retiros" element={<MemberPlaceholder overline="Retiros" title="Retiros e encontros" body="Calendário e inscrição nos próximos retiros, dias de deserto e encontros locais." />} />
        <Route path="formacao" element={<Navigate to="/membros/biblioteca" replace />} />
        <Route path="diretorio" element={<MemberDirectory />} />
        <Route path="convites" element={<MemberInvites />} />
        <Route path="horario-espiritual" element={<MemberSpiritual />} />
        <Route path="santuarios" element={<MemberShrines />} />
        <Route path="capital-de-gracas" element={<MemberIntentions />} />
        <Route path="acompanhamento" element={<MemberSpiritualAssessor />} />
      </Route>

      {/* Public */}
      <Route path="/" element={<PublicShell><Home /></PublicShell>} />
      <Route path="/sobre" element={<PublicShell><About /></PublicShell>} />
      <Route path="/comunidades" element={<PublicShell><CommunitiesPage /></PublicShell>} />
      <Route path="/comunidades/:slug" element={<PublicShell><CommunityDetailPage /></PublicShell>} />
      <Route path="/espiritualidade" element={<PublicShell><Spirituality /></PublicShell>} />
      <Route path="/verificar-certificado" element={<PublicShell><VerifyCertificate /></PublicShell>} />
      <Route path="/cartao/:slug" element={<BusinessCard />} />
      <Route path="/apresentacao-setembro-2026" element={<PublicShell><PresentationSept2026 /></PublicShell>} />
      <Route path="/congresso-miami-2026" element={<PublicShell><MiamiCongress /></PublicShell>} />
      <Route path="/eventos" element={<PublicShell><Events /></PublicShell>} />
      <Route path="/eventos/:slug" element={<PublicShell><EventDetail /></PublicShell>} />
      <Route path="/contactos" element={<PublicShell><Contact /></PublicShell>} />
      <Route path="/apoiar" element={<PublicShell><Support /></PublicShell>} />
      <Route path="/apoiar/renovar" element={<PublicShell><RenewPledge /></PublicShell>} />
      <Route path="/donativo-confirmado" element={<PublicShell><DonationSuccess /></PublicShell>} />
      <Route path="/inscricao-confirmada" element={<PublicShell><PaymentSuccess /></PublicShell>} />
      <Route path="/convite/:token" element={<PublicShell><InviteLanding /></PublicShell>} />
      <Route path="/convocatoria/:token" element={<PublicShell><ConvocationRespond /></PublicShell>} />
      <Route path="*" element={<PublicShell><NotFound /></PublicShell>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <MemberAuthProvider>
            <BrowserRouter>
              <ScrollToTop />
              <AppRouter />
              <MTAChatGate />
              <Toaster
                position="top-center"
                toastOptions={{ style: { background: "#FDFBF7", border: "1px solid #E5E0D8", color: "#1A201F", fontFamily: "Outfit, sans-serif" } }}
              />
            </BrowserRouter>
          </MemberAuthProvider>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
